import React, { useContext, useEffect, useRef, useState } from "react";
import style from "./index.module.scss";
import {
    Alert,
    Button,
    Icon,
    Input,
    Menu,
    message,
    Modal,
    Popover,
    Spin,
    Tooltip
} from "antd";
import {
    adjustRandomGroupName,
    deleteGroup,
    deleteRandomGroup,
    GroupType,
    updateGroup
} from "service/group";
import CustomEmpty from "components/CourseComponents/CustomEmpty";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage } from "service/service";
import useModalHandler from "utils/useModalHandler";
import AutoGroupModal from "./Components/AutoGroupModal";
import { useAtom, useSetAtom } from "jotai";
import { FlagAtom, groupIdAtom, GroupSettingsContext } from "../../store";
import { useParams } from "react-router-dom";
import SubmitError from "components/SubmitError";

export interface Props {
    type?: GroupType;
    randomGroupKey?: string;
    canEdit?: boolean;
    signInId?: number;
}
export default function GroupsList({
    randomGroupKey,
    canEdit = true,
    signInId,
    type = GroupType.base
}: Props) {
    const addModal = useModalHandler();
    const { flag } = useParams();

    //通过活动列表页点击快速创建小组时弹窗才显示
    useEffect(() => {
        if (flag === "add") {
            addModal.open();
        }
    }, [flag]);

    const {
        groupList,
        groupListLoading,
        updateGroupList,
        setSearch,
        updateNoGroupStudent
    } = useContext(GroupSettingsContext);

    const [name, setName] = useState(""); //小组名
    const [curId, setCurId] = useState(-1); //当前选中的id
    const [selectedGroupId, setSelectedGroupId] = useAtom(groupIdAtom);
    const updateFlag = useSetAtom(FlagAtom);

    useEffect(() => {
        if (!groupList || groupList?.length === 0) {
            setSelectedGroupId(-1);
        } else {
            const flag = groupList.findIndex(
                item => item.id === selectedGroupId
            );
            if (flag === -1) {
                setSelectedGroupId(groupList[0].id);
            } else {
                setSelectedGroupId(selectedGroupId);
            }
        }
    }, [groupList, selectedGroupId]);

    //查询
    const onSearch = (value: string) => {
        setSearch(value);
        updateFlag(true);
    };

    //一键分组成功
    const onAddOk = () => {
        addModal.close();
        updateGroupList();
    };

    //重命名
    const onEdit = (id: number, name: string) => {
        setName(name);
        setCurId(id);
    };
    const onUpdateName = async (id: number) => {
        try {
            switch (type) {
                case GroupType.base:
                    await updateGroup(id, name);
                    break;
                case GroupType.random:
                    await adjustRandomGroupName(id, name, randomGroupKey!);
                    break;
            }
            message.success("修改成功");
            updateGroupList();
            setCurId(-1);
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="修改失败" content={errorMessage(e)} />,
                { width: 300 }
            );
        }
    };

    //删除小组
    function handleDelete(id: number) {
        Modal.confirm({
            title: `确认删除小组？将同时删除小组成员信息、已发生的小组活动及成绩数据，且无法恢复。`,
            onOk: async () => {
                try {
                    switch (type) {
                        case GroupType.base:
                            await deleteGroup(id);
                            break;
                        case GroupType.random:
                            await deleteRandomGroup(id, randomGroupKey!);
                            break;
                    }
                    await updateNoGroupStudent();
                    await updateGroupList();
                    updateFlag(false);
                    message.success("删除成功");
                } catch (e) {
                    ResultMessage.error(
                        <SubmitError
                            title="删除失败"
                            content={errorMessage(e)}
                        />,
                        { width: 300 }
                    );
                }
            }
        });
    }

    const ref = useRef<HTMLDivElement>(null);

    return (
        <div className={style.root} ref={ref}>
            {type === GroupType.base && (
                <Alert
                    message="鼠标滑过小组右侧 … 可以进行重命名和删除"
                    type="warning"
                    showIcon
                    closable
                />
            )}
            {canEdit && (
                <div className={style.flexRight}>
                    <Button
                        type="primary"
                        ghost
                        className={style.addBtn}
                        onClick={addModal.open}>
                        <img src={require("./img/addIcon.png")} />
                        一键分组
                    </Button>
                </div>
            )}
            {type === GroupType.base && (
                <Input.Search
                    className={style.search}
                    placeholder="请输入小组名称"
                    onSearch={value => onSearch(value)}
                />
            )}

            <div className={style.list}>
                <Spin spinning={groupListLoading}>
                    <Menu
                        selectedKeys={[selectedGroupId.toString()]}
                        onSelect={item => setSelectedGroupId(Number(item.key))}>
                        {groupList &&
                            groupList?.map(item => (
                                <Menu.Item
                                    key={item.id}
                                    className={style.menuItem}>
                                    {item.id === curId && (
                                        <div className={style.edit}>
                                            <Input
                                                className={style.inputItem}
                                                placeholder="请输入组名"
                                                defaultValue={item.groupName}
                                                maxLength={50}
                                                onChange={e =>
                                                    setName(e.target.value)
                                                }
                                            />
                                            <Icon
                                                type="check"
                                                onClick={() =>
                                                    onUpdateName(item.id)
                                                }
                                            />
                                            <Icon
                                                type="close"
                                                onClick={() => setCurId(-1)}
                                            />
                                        </div>
                                    )}
                                    {item.id !== curId && (
                                        <>
                                            <div className={style.title}>
                                                <Tooltip
                                                    title={item.groupName}
                                                    placement="topLeft">
                                                    <div className={style.name}>
                                                        {item.groupName}
                                                        &nbsp;&nbsp;
                                                    </div>
                                                </Tooltip>
                                                <span>
                                                    ({item.memberCount}人)
                                                </span>
                                            </div>

                                            {canEdit && (
                                                <Popover
                                                    getPopupContainer={() =>
                                                        ref.current!
                                                    }
                                                    content={
                                                        <div
                                                            className={
                                                                style.popover
                                                            }>
                                                            <button
                                                                onClick={() =>
                                                                    onEdit(
                                                                        item.id,
                                                                        item.groupName
                                                                    )
                                                                }>
                                                                重命名
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        item.id
                                                                    )
                                                                }>
                                                                删除
                                                            </button>
                                                        </div>
                                                    }
                                                    placement="bottom"
                                                    overlayClassName={
                                                        style.popoverWrapper
                                                    }>
                                                    <Icon type="ellipsis" />
                                                </Popover>
                                            )}
                                        </>
                                    )}
                                </Menu.Item>
                            ))}
                    </Menu>
                    {(!groupList || groupList?.length === 0) && (
                        <CustomEmpty
                            description="无小组"
                            style={{ margin: "100px 0" }}
                        />
                    )}
                </Spin>
            </div>

            {addModal.render(
                <AutoGroupModal
                    signInId={signInId}
                    groupType={type}
                    visible={addModal.visible}
                    onAddOk={onAddOk}
                    randomGroupKey={randomGroupKey}
                    onCancel={addModal.close}
                />
            )}
        </div>
    );
}
